import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { 
  TrendingUp, 
  Target, 
  Award, 
  BarChart3, 
  Flame,
  Shield,
  CheckCircle2,
  XCircle,
  Info,
  Calendar,
  Clock,
  ChevronRight
} from 'lucide-react';
import { useSEO, SEO_KEYWORDS } from '../utils/seo';
import LeagueAccuracyModal from '../components/LeagueAccuracyModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccuracyReport() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [timeBasedStats, setTimeBasedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [showLeagueModal, setShowLeagueModal] = useState(false);

  // SEO for accuracy page
  useSEO({
    title: 'Prediction Accuracy & Track Record',
    description: 'View our sports prediction accuracy by league. SportsAnalysisAB achieves 60-80% prediction success rate across NHL, NBA, NFL, MLB. Transparent results and verified picks.',
    keywords: SEO_KEYWORDS.accuracy,
    canonicalUrl: 'https://sportsanalysisab.ca/accuracy'
  });

  const openLeagueAccuracy = (league) => {
    setSelectedLeague(league);
    setShowLeagueModal(true);
  };

  useEffect(() => {
    fetchAccuracy();
    fetchTimeBasedAccuracy();
  }, []);

  const fetchAccuracy = async () => {
    try {
      const endpoint = isAdmin ? '/api/accuracy/detailed' : '/api/accuracy';
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 401 || response.status === 403) {
        // Fall back to public endpoint if admin endpoint fails
        const publicResponse = await fetch(`${API_URL}/api/accuracy`);
        if (publicResponse.ok) {
          const data = await publicResponse.json();
          setStats(data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch accuracy:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeBasedAccuracy = async () => {
    try {
      const response = await fetch(`${API_URL}/api/accuracy/time-based`);
      if (response.ok) {
        const data = await response.json();
        setTimeBasedStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch time-based accuracy:', err);
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 70) return 'text-green-400';
    if (accuracy >= 55) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getAccuracyBadge = (accuracy) => {
    if (accuracy >= 70) return { text: 'Excellent', class: 'bg-green-500/10 text-green-400 border-green-500/20' };
    if (accuracy >= 60) return { text: 'Good', class: 'bg-blue-500/10 text-blue-400 border-blue-500/20' };
    if (accuracy >= 55) return { text: 'Average', class: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' };
    return { text: 'Building Data', class: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-4" />
          <Skeleton className="h-6 w-96 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
            <Skeleton className="h-40" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const badge = stats ? getAccuracyBadge(stats.accuracy_percentage) : { text: 'N/A', class: '' };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-10 h-10 text-red-400" />
              <div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">
                  Platform Accuracy
                </h1>
                <p className="text-muted-foreground">
                  Historical prediction performance across all sports
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Main Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Overall Accuracy */}
            <div className="bg-card border border-border rounded-sm p-6 col-span-1 md:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-500/10 rounded-sm flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h2 className="font-display text-lg font-bold uppercase tracking-tight">
                      Overall Accuracy
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Based on {stats?.total_predictions || 0} completed predictions
                    </p>
                  </div>
                </div>
                <Badge className={`text-sm ${badge.class}`}>
                  {badge.text}
                </Badge>
              </div>
              
              <div className="flex items-baseline gap-2 mb-4">
                <span className={`font-mono text-6xl font-bold ${getAccuracyColor(stats?.accuracy_percentage || 0)}`}>
                  {(stats?.accuracy_percentage || 0).toFixed(1)}%
                </span>
                <span className="text-muted-foreground">accuracy rate</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Correct Predictions</span>
                  <span className="font-mono text-green-400">{stats?.correct_predictions || 0}</span>
                </div>
                <Progress 
                  value={stats?.accuracy_percentage || 0} 
                  className="h-3"
                />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Incorrect Predictions</span>
                  <span className="font-mono text-red-400">
                    {(stats?.total_predictions || 0) - (stats?.correct_predictions || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Streak */}
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-500/10 rounded-sm flex items-center justify-center">
                  <Flame className="w-6 h-6 text-orange-400" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold uppercase tracking-tight">
                    Hot Streak
                  </h2>
                  <p className="text-xs text-muted-foreground">Recent correct picks</p>
                </div>
              </div>
              <div className="text-center">
                <span className="font-mono text-5xl font-bold text-orange-400">
                  {stats?.recent_streak || 0}
                </span>
                <p className="text-sm text-muted-foreground mt-2">consecutive wins</p>
              </div>
            </div>
          </div>

          {/* Accuracy by League */}
          <div className="bg-card border border-border rounded-sm p-6 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-6 h-6 text-blue-400" />
              <h2 className="font-display text-xl font-bold uppercase tracking-tight">
                Accuracy by League
              </h2>
            </div>
            
            {stats?.by_league && Object.keys(stats.by_league).length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(stats.by_league).map(([league, data]) => (
                  <button 
                    key={league}
                    onClick={() => openLeagueAccuracy(league)}
                    className="bg-secondary/30 rounded-sm p-4 text-left hover:bg-secondary/50 transition-colors cursor-pointer group"
                    data-testid={`league-accuracy-${league}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-display font-bold uppercase text-sm">{league.toUpperCase()}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {data.total} games
                        </span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className={`font-mono text-2xl font-bold ${getAccuracyColor(data.accuracy)}`}>
                        {data.accuracy.toFixed(1)}%
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <CheckCircle2 className="w-3 h-3 text-green-400" />
                      <span>{data.correct}</span>
                      <XCircle className="w-3 h-3 text-red-400 ml-2" />
                      <span>{data.total - data.correct}</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No league data available yet. Predictions will appear after games are completed.
              </p>
            )}
          </div>

          {/* Time-Based Accuracy - Daily & Weekly */}
          {timeBasedStats && (
            <div className="bg-card border border-border rounded-sm p-6 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-6 h-6 text-purple-400" />
                <h2 className="font-display text-xl font-bold uppercase tracking-tight">
                  Daily & Weekly Performance
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Daily Stats */}
                <div className="bg-secondary/30 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <h3 className="font-display font-bold uppercase text-sm">Last 24 Hours</h3>
                  </div>
                  
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className={`font-mono text-4xl font-bold ${getAccuracyColor(timeBasedStats.daily?.accuracy_percentage || 0)}`}>
                      {(timeBasedStats.daily?.accuracy_percentage || 0).toFixed(1)}%
                    </span>
                    <span className="text-sm text-muted-foreground">
                      ({timeBasedStats.daily?.correct_predictions || 0}/{timeBasedStats.daily?.total_predictions || 0})
                    </span>
                  </div>
                  
                  {/* Daily by League */}
                  {timeBasedStats.daily?.by_league && Object.keys(timeBasedStats.daily.by_league).length > 0 && (
                    <div className="space-y-2 border-t border-border pt-4">
                      <p className="text-xs text-muted-foreground mb-2">By League:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(timeBasedStats.daily.by_league).map(([league, data]) => (
                          data.total > 0 && (
                            <div key={league} className="flex items-center justify-between text-xs">
                              <span className="font-display font-bold">{league}</span>
                              <span className={`font-mono ${getAccuracyColor(data.accuracy)}`}>
                                {data.accuracy.toFixed(0)}% ({data.correct}/{data.total})
                              </span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Weekly Stats */}
                <div className="bg-secondary/30 rounded-sm p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-green-400" />
                    <h3 className="font-display font-bold uppercase text-sm">Last 7 Days</h3>
                  </div>
                  
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className={`font-mono text-4xl font-bold ${getAccuracyColor(timeBasedStats.weekly?.accuracy_percentage || 0)}`}>
                      {(timeBasedStats.weekly?.accuracy_percentage || 0).toFixed(1)}%
                    </span>
                    <span className="text-sm text-muted-foreground">
                      ({timeBasedStats.weekly?.correct_predictions || 0}/{timeBasedStats.weekly?.total_predictions || 0})
                    </span>
                  </div>
                  
                  {/* Weekly by League */}
                  {timeBasedStats.weekly?.by_league && Object.keys(timeBasedStats.weekly.by_league).length > 0 && (
                    <div className="space-y-2 border-t border-border pt-4">
                      <p className="text-xs text-muted-foreground mb-2">By League:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(timeBasedStats.weekly.by_league).map(([league, data]) => (
                          data.total > 0 && (
                            <div key={league} className="flex items-center justify-between text-xs">
                              <span className="font-display font-bold">{league}</span>
                              <span className={`font-mono ${getAccuracyColor(data.accuracy)}`}>
                                {data.accuracy.toFixed(0)}% ({data.correct}/{data.total})
                              </span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* All-Time Summary */}
              {timeBasedStats.all_time && timeBasedStats.all_time.total_predictions > 0 && (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-red-400" />
                      <span className="font-display font-bold uppercase text-sm">All-Time</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className={`font-mono text-2xl font-bold ${getAccuracyColor(timeBasedStats.all_time.accuracy_percentage)}`}>
                        {timeBasedStats.all_time.accuracy_percentage.toFixed(1)}%
                      </span>
                      <span className="text-sm text-muted-foreground">
                        ({timeBasedStats.all_time.correct_predictions}/{timeBasedStats.all_time.total_predictions} predictions)
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Accuracy by Confidence */}
          <div className="bg-card border border-border rounded-sm p-6 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-6 h-6 text-green-400" />
              <h2 className="font-display text-xl font-bold uppercase tracking-tight">
                Accuracy by Confidence Level
              </h2>
            </div>
            
            {stats?.by_confidence && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {['High', 'Medium', 'Low'].map((level) => {
                  const data = stats.by_confidence[level] || { total: 0, correct: 0, accuracy: 0 };
                  return (
                    <div key={level} className="text-center">
                      <Badge className={`mb-3 ${
                        level === 'High' ? 'confidence-high' :
                        level === 'Medium' ? 'confidence-medium' : 'confidence-low'
                      }`}>
                        {level} Confidence
                      </Badge>
                      <div className={`font-mono text-4xl font-bold ${getAccuracyColor(data.accuracy)}`}>
                        {data.accuracy.toFixed(1)}%
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {data.correct} / {data.total} correct
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {level === 'High' ? 'Strong indicators, high data quality' :
                         level === 'Medium' ? 'Mixed signals, moderate certainty' :
                         'Limited data, higher uncertainty'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Disclaimer */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-sm p-6">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display font-bold uppercase tracking-tight text-blue-400 mb-2">
                  About Our Accuracy
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Our predictions are based on historical team performance, home advantage, recent form, 
                  and other statistical factors. Accuracy percentages reflect how often our predicted 
                  winner (the team with higher probability) actually won the game.
                </p>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    For informational and entertainment purposes only. Past performance does not 
                    guarantee future results. Not betting advice.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      
      {/* League Accuracy Modal */}
      <LeagueAccuracyModal
        isOpen={showLeagueModal}
        onClose={() => setShowLeagueModal(false)}
        leagueId={selectedLeague}
        leagueName={selectedLeague?.toUpperCase()}
      />
    </div>
  );
}
